import { Button } from "@root/shared/components/Button";
import { TagButton } from "@root/shared/components/TagButton";
import { ModalTrigger } from "@root/shared/components/ModalTrigger";
import { ChoiceChip } from "@root/shared/components/ChoiceChip";
import { useMemo } from "react";
import { Heading } from "@userFrontend/components/Heading";
import * as styles from "./styles.css";
import { type Choice } from "../../types";

export type BusinessTypeOptions = {
  businessTypeCategoryName: string;
  choices: Choice[];
};

type Props = {
  values: string[];
  onChange: (values: string[]) => void;
  businessTypeOptions: BusinessTypeOptions[];
};

export const BusinessTypeSelector = ({ values, onChange, businessTypeOptions }: Props) => {
  const allChoices = useMemo(
    () => businessTypeOptions.flatMap((bt) => bt.choices),
    [businessTypeOptions],
  );

  const labelToValueMap = useMemo(() => {
    return new Map(allChoices.map((choice) => [choice.label, choice.value]));
  }, [allChoices]);

  const selectedLabels = useMemo(() => {
    return allChoices
      .filter((choice) => values.includes(choice.value))
      .map((choice) => choice.label);
  }, [allChoices, values]);

  const handleRemoveTag = (labelToRemove: string) => {
    const valueToRemove = labelToValueMap.get(labelToRemove);
    if (valueToRemove) {
      onChange(values.filter((value) => value !== valueToRemove));
    }
  };

  return (
    <>
      <div className={styles.tagButtonWrapper}>
        {selectedLabels.map((label) => (
          <TagButton key={label} theme="removable" onClick={() => handleRemoveTag(label)}>
            {label}
          </TagButton>
        ))}
      </div>
      <ModalTrigger
        spAppearanceType="bottom"
        headerTitle="職種を選択"
        renderFooterContent={(close) => (
          <Button theme="secondary" size="large" className={styles.footerButton} onClick={close}>
            戻る
          </Button>
        )}
        footerContentStyle="button"
        renderAction={(open) => (
          <Button theme="secondary" size="large" className={styles.selectButton} onClick={open}>
            <span className={styles.buttonHighlightText}>職種</span>を選択
          </Button>
        )}
      >
        <>
          {businessTypeOptions.map((businessType) => (
            <div key={businessType.businessTypeCategoryName} className={styles.businessTypes}>
              <Heading tag="p" size="small">
                {businessType.businessTypeCategoryName}
              </Heading>
              <div className={styles.choiceChipWrapper}>
                <ChoiceChip
                  name="q[business_type][]"
                  values={values}
                  choices={businessType.choices}
                  onChange={onChange}
                />
              </div>
            </div>
          ))}
        </>
      </ModalTrigger>
    </>
  );
};
